/**
 * Cookie-Consent-Mechanik mit STRIKTER Google Consent Mode v2.
 *
 * Architektur (P0-Fix 25.06.2026 — auf Bug-Meldung der Ads-Agentur):
 *  1. index.html lädt NUR den Consent-Default-Block
 *     `gtag('consent', 'default', { ad_*: denied, analytics_storage: denied … })`
 *     mit `ads_data_redaction=true` und `url_passthrough=true`.
 *     GTM, GA4, Google Ads, Meta Pixel und PostHog werden **nicht** geladen.
 *  2. Erst wenn der Nutzer im Cookie-Banner mindestens eine optionale Kategorie
 *     aktiviert (Statistik ODER Marketing), wird:
 *        a) `gtag('consent','update', …)` mit den entsprechenden Signalen gefeuert
 *        b) der Google-Tag-Manager-Container DYNAMISCH nachgeladen
 *        c) optional Meta Pixel und PostHog injiziert
 *  3. Lehnt der Nutzer ab oder schließt das Banner ohne Aktion, werden KEINE
 *     externen Skripte geladen — **null Requests** an googletagmanager.com,
 *     doubleclick.net, connect.facebook.net oder posthog.com.
 *
 * Damit ist garantiert, dass die Ads-Agentur vor User-Consent **keinerlei**
 * Tracker-Aktivität in den Logs sieht — auch keine Cookieless-Consent-Mode-Pings.
 */
const CONSENT_KEY = 'gle_cookie_consent';

export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

export function setConsent(consent) {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...consent, timestamp: new Date().toISOString() }));
  applyConsent(consent);
}

export function hasConsented() {
  return getConsent() !== null;
}

/**
 * Hauptanwendung des Consents. Wird beim Banner-Klick UND beim Page-Load mit
 * gespeichertem Consent aufgerufen.
 */
export function applyConsent(consent) {
  if (!consent) return;

  // 1) Google Consent Mode v2 — die 4 v2-Signale aktualisieren.
  pushConsentUpdate({
    ad_storage:         consent.marketing  ? 'granted' : 'denied',
    ad_user_data:       consent.marketing  ? 'granted' : 'denied',
    ad_personalization: consent.marketing  ? 'granted' : 'denied',
    analytics_storage:  consent.statistics ? 'granted' : 'denied',
  });

  // 2) GTM erst NACH expliziter Zustimmung zu mindestens einer Kategorie laden.
  //    Solange beide optionale Kategorien `false` sind, bleibt GTM blockiert
  //    → keine doubleclick-/googletagmanager-Requests.
  if (consent.marketing || consent.statistics) {
    loadGTM();
  }

  // 3) Drittanbieter, die NICHT über GTM laufen, manuell injizieren.
  if (consent.marketing)  loadMetaPixel();
  if (consent.marketing)  loadOpenAIPixel();
  if (consent.statistics) loadPostHog();
}

function pushConsentUpdate(signals) {
  try {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', signals);
    } else {
      window.dataLayer.push(['consent', 'update', signals]);
    }
  } catch (_e) { /* swallow */ }
}

/**
 * Lädt den Google-Tag-Manager-Container dynamisch nach Consent.
 * Idempotent: zweimaliger Aufruf führt nicht zu doppelter Initialisierung.
 */
function loadGTM() {
  if (window.__gle_gtm_loaded) return;
  const id = window.__GTM_ID;
  if (!id) return;
  window.__gle_gtm_loaded = true;
  /* eslint-disable */
  (function(w,d,s,l,i){
    w[l]=w[l]||[];
    w[l].push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
    var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),
        dl=l!='dataLayer'?'&l='+l:'';
    j.async=true;
    j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
    f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer',id);
  /* eslint-enable */
}

function loadMetaPixel() {
  if (window.__gle_meta_loaded) return;
  window.__gle_meta_loaded = true;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
  n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
  (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', '1427576708877153');
  window.fbq('track', 'PageView');
}

/**
 * OpenAI Ads Measurement Pixel (Datenquelle „Green Lion Energy – Website“).
 * Offizielles Setup-Snippet (developers.openai.com/ads/measurement-pixel),
 * nur nach Marketing-Consent, idempotent (nur EINE Pixel-Instanz).
 */
function loadOpenAIPixel() {
  if (window.__gle_oai_loaded) return;
  // Feature-Flag: Pixel nur laden, wenn explizit freigegeben (Prod-Freigabe steht aus).
  if (process.env.REACT_APP_OPENAI_PIXEL_ENABLED !== 'true') return;
  const pixelId = process.env.REACT_APP_OPENAI_PIXEL_ID;
  if (!pixelId) return;
  window.__gle_oai_loaded = true;
  try {
    /* eslint-disable */
    (function (w, d, s, u) {
      if (w.oaiq) return;
      var q = function () { q.q.push(arguments); };
      q.q = [];
      w.oaiq = w.oaiq || q;
      var js = d.createElement(s);
      js.async = true;
      js.src = u;
      var f = d.getElementsByTagName(s)[0];
      f.parentNode.insertBefore(js, f);
    })(window, document, 'script', 'https://bzrcdn.openai.com/sdk/oaiq.min.js');
    /* eslint-enable */
    window.oaiq('init', { pixelId: pixelId, debug: false });
  } catch (_e) { /* Tracking darf die Seite nie beeinträchtigen */ }
}

function loadPostHog() {
  if (window.__gle_stats_loaded) return;
  window.__gle_stats_loaded = true;
  /* eslint-disable */
  !(function(t,e){var o,n,p,r;e.__SV||((window.posthog=e),(e._i=[]),(e.init=function(i,s,a){
  function g(t,e){var o=e.split(".");2==o.length&&((t=t[o[0]]),(e=o[1])),
  (t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))});}
  ((p=t.createElement("script")).type="text/javascript"),(p.crossOrigin="anonymous"),
  (p.async=!0),(p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js"),
  (r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;
  for(void 0!==a?(u=e[a]=[]):(a="posthog"),u.people=u.people||[],
  u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},
  u.people.toString=function(){return u.toString(1)+".people (stub)"},
  o="init capture register register_once unregister identify setPersonProperties group reset get_distinct_id getGroups get_session_id opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),
  n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])}),(e.__SV=1))})(document,window.posthog||[]);
  /* eslint-enable */
  window.posthog.init('phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE',
    { api_host: 'https://us.i.posthog.com', person_profiles: 'identified_only' });
}

// Beim Seitenaufruf: gespeicherten Consent erneut anwenden (Signale updaten,
// GTM/Pixel/PostHog ggf. nachladen).
const saved = getConsent();
if (saved) applyConsent(saved);
